import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import updateAllPadding from '../updateAllPadding';
import VideoInput from '../../../../componentLibrary/VideoInput';
import ElementPadding from '../../../../componentLibrary/ElementPadding';
import paddingChange from '../../paddingChange';
import eventService from '../../../../common/eventService';
import HorizontalRule from '../../../../componentLibrary/HorizontalRule';
import * as columnItemActions from '../../../../actions/columnItem';
import * as Styled from '../styles';
import { elementContentType } from '../../../../types';
import { ERR_IMAGE_UPLOAD_LINK, ERR_IMAGE_UPLOAD_LOAD } from '../../../../constants/Errors';

const imageARCache = {};
const _getImageAspectRatio = (src) =>
    new Promise((resolve, reject) => {
        if (src === 'PLACE_HOLDER') {
            resolve(null);
            return;
        }
        // eslint-disable-next-line no-prototype-builtins
        if (imageARCache.hasOwnProperty(src)) {
            resolve(imageARCache[src]);
            return;
        }

        const img = new Image();
        // eslint-disable-next-line func-names
        img.onload = function () {
            if (+this.width > 0 && +this.height > 0) {
                imageARCache[src] = parseFloat(+this.width / +this.height);
                resolve(imageARCache[src]);
            } else {
                reject(new Error(ERR_IMAGE_UPLOAD_LINK));
            }
        };
        img.onerror = (err, data) => {
            // eslint-disable-next-line no-console
            console.error(err, data);
            reject(new Error(ERR_IMAGE_UPLOAD_LOAD));
        };
        img.src = src;
    });

class VideoSetting extends PureComponent {
    state = {
        blockIndex: null,
        columnIdx: null,
        elmIdx: null,
        content: null,
        contentValues: {
            aspectRatio: null,
            href: '',
            playButtonColor: '',
            rawSource: '',
            paddingTop: 0,
            paddingRight: 0,
            paddingBottom: 0,
            paddingLeft: 0,
        },
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { selectedElementData } = nextProps;
        const { blockIndex, columnIdx, elmIdx, content } = selectedElementData;
        const {
            src,
            aspectRatio,
            href,
            playButtonColor,
            rawSource,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
        } = content;
        const contentValues = {
            src,
            aspectRatio,
            href,
            playButtonColor,
            rawSource,
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
        };
        const propsChanged =
            elmIdx !== prevState.elmIdx || JSON.stringify(contentValues) !== JSON.stringify(prevState.contentValues);
        if (propsChanged) {
            return { ...prevState, elmIdx, blockIndex, columnIdx, content, contentValues };
        }
        return { ...prevState };
    }

    _updatedAspectRatio(key, value) {
        const { blockIndex, columnIdx, elmIdx, content, contentValues } = this.state;
        const { dispatch } = this.props;
        (async () => {
            try {
                const newContentValues = {
                    ...contentValues,
                    [key]: value,
                    aspectRatio: value.length > 0 ? await _getImageAspectRatio(value) : null,
                };
                const newContent = { ...content, ...newContentValues };
                dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
                this.setState((prevState) => ({
                    ...prevState,
                    contentValues: newContentValues,
                }));
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                eventService.notify(ERR_IMAGE_UPLOAD_LINK, true);
            }
        })();
    }

    _updateContent(key, value) {
        const { blockIndex, columnIdx, elmIdx, content, contentValues } = this.state;
        const { dispatch } = this.props;
        if (key === 'rawSource') {
            this._updatedAspectRatio(key, value);
        } else {
            let newContentValues;
            if (key === 'updateAllPadding') {
                newContentValues = updateAllPadding(contentValues, value);
            } else {
                newContentValues = { ...contentValues, [key]: value };
            }
            const newContent = { ...content, ...newContentValues };
            dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
            this.setState((prevState) => ({
                ...prevState,
                contentValues: newContentValues,
            }));
        }
    }

    _deleteVideo() {
        const { blockIndex, columnIdx, elmIdx, content, contentValues } = this.state;
        const { dispatch } = this.props;
        const newContentValues = {
            ...contentValues,
            rawSource: '',
            href: '',
            playButtonColor: 'white',
        };
        const newContent = { ...content, ...newContentValues };
        dispatch(columnItemActions.updateColumnItemContent({ blockIndex, columnIdx, elmIdx, newContent }));
        this.setState((prevState) => ({
            ...prevState,
            contentValues: newContentValues,
        }));
    }

    render() {
        const { elmIdx, contentValues } = this.state;
        const { href, playButtonColor, rawSource, paddingTop, paddingRight, paddingBottom, paddingLeft } =
            contentValues;

        return (
            <div>
                <Styled.Row>
                    <HorizontalRule />
                    <ElementPadding
                        paddingTop={paddingTop}
                        paddingBottom={paddingBottom}
                        paddingLeft={paddingLeft}
                        paddingRight={paddingRight}
                        elementType="video"
                        onChange={(event) => {
                            const payload = paddingChange({
                                event,
                                paddingBottom,
                                paddingLeft,
                                paddingRight,
                                paddingTop,
                            });
                            if (!payload) {
                                return;
                            }
                            const { key, value = 0 } = payload;
                            this._updateContent(key, value);
                        }}
                    />
                    <HorizontalRule />
                    <VideoInput
                        index={`${elmIdx}-video`}
                        href={href}
                        playButtonColor={playButtonColor}
                        rawSource={rawSource}
                        onChange={async (values) => {
                            if (values.href !== undefined && values.href !== href) {
                                await this._updateContent('href', values.href);
                            }
                            if (values.playButtonColor !== undefined && values.playButtonColor !== playButtonColor) {
                                await this._updateContent('playButtonColor', values.playButtonColor);
                            }
                            if (values.rawSource !== undefined && values.rawSource !== rawSource) {
                                await this._updateContent('rawSource', values.rawSource);
                            }
                        }}
                        onDelete={() => {
                            this._deleteVideo();
                        }}
                    />
                </Styled.Row>
            </div>
        );
    }
}

VideoSetting.propTypes = {
    dispatch: PropTypes.func.isRequired,
    selectedElementData: PropTypes.shape({
        blockIndex: PropTypes.string.isRequired,
        columnIdx: PropTypes.string.isRequired,
        elmIdx: PropTypes.string.isRequired,
        content: elementContentType.isRequired,
    }).isRequired,
};

export default connect()(VideoSetting);
